import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button,
  Autocomplete,
  Switch,
  FormControlLabel,
  MenuItem
} from "@mui/material";
import api from "../services/api";
import { openAuthPdf } from "../services/openAuthPdf";
import { todayISO } from "../utils/date";

const UNITS = ["Lbs", "Kg", "MT"];

/* ===== MAIN COMPONENT ===== */
const todayStr = () => todayISO();
const daysAgoStr = (n) => new Date(Date.now() - n * 86400000).toISOString().split("T")[0];

export default function SaleOrders() {
  const [editId, setEditId] = useState(null);
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ from: daysAgoStr(90), to: todayStr(), name: "" });

  // Load accounts and products once
  const loadDropdowns = async () => {
    try {
      const accRes = await api.get("/salesOrders/accounts");
      setAccounts(Array.isArray(accRes.data) ? accRes.data : []);

      const prodRes = await api.get("/salesOrders/products");
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
    } catch (err) {
      console.error("Dropdown load error:", err);
    }
  };

  // Load sales orders
  const loadSalesOrders = async (f = filters) => {
    try {
      const res = await api.get("/salesOrders", {
        params: { from: f.from || undefined, to: f.to || undefined, name: f.name || undefined },
      });
      if (!Array.isArray(res.data)) return;

      setRows(
        res.data.map(r => ({
          Master_ID: r.Master_ID,
          Customer_ID: r.Customer_ID,
          Name: r.Name,
          Date: r.Date ? r.Date.split("T")[0] : "",
          Item: r.Item_Description || "",
          SO: r.SO || "",
          Qty: r.Qty || 0,
          Unit: r.Unit || "",
          Price: r.Price === null || r.Price === undefined ? "" : r.Price,
          Active: r.Active // ✅ include Active
        }))
      );
    } catch (err) {
      console.error("Failed to load sales orders:", err);
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadSalesOrders();
    // Load once on mount; loaders are stable for this view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <SaleOrderEditor
        editId={editId}
        setEditId={setEditId}
        reload={loadSalesOrders}
        accounts={accounts}
        products={products}
      />
      <SaleOrderList
        rows={rows}
        onEdit={setEditId}
        reload={loadSalesOrders}
        filters={filters}
        setFilters={setFilters}
        accounts={accounts}
      />
    </Box>
  );
}

/* ===== Filter bar shared by both order lists ===== */
function OrderFilterBar({ filters, setFilters, onSearch, accounts }) {
  const names = (accounts || []).map((a) => a.SubAccount_Name).filter(Boolean);
  return (
    <Paper sx={{ p: 2, mb: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            freeSolo
            options={names}
            inputValue={filters.name}
            onInputChange={(e, val) => setFilters({ ...filters, name: val })}
            onChange={(e, val) => { const nf = { ...filters, name: val || "" }; setFilters(nf); onSearch(nf); }}
            renderInput={(params) => (
              <TextField {...params} label="Customer (type to search)" fullWidth
                onKeyDown={(e) => { if (e.key === "Enter") onSearch({ ...filters }); }} />
            )}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth
            value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth
            value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <Button variant="contained" fullWidth onClick={() => onSearch()}>Search</Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ===== SALE ORDER EDITOR ===== */
function SaleOrderEditor({ editId, setEditId, reload, accounts, products }) {
  const [master, setMaster] = useState({
    Master_ID: "",
    CustomerObj: null,
    ItemObj: null,
    Date: "",
    SO: "",
    Qty: 0,
    Unit: "Lbs",
    Price: "",
    Active: true
  });

  // Load sale order for editing
  useEffect(() => {
    if (!editId) {
      setMaster({
        Master_ID: "",
        CustomerObj: null,
        ItemObj: null,
        Date: "",
        SO: "",
        Qty: 0,
        Unit: "Lbs",
        Price: "",
        Active: true
      });
      return;
    }

    const loadEditData = async () => {
      try {
        const res = await api.get(`/salesOrders/${editId}`);
        const data = res.data;
        const details = data.details?.[0] || {};

        const customerObj =
          accounts.find(a => a.id === data.master.Customer_ID) || null;
        const itemObj =
          products.find(p => p.Item_ID === details.Item_ID) || null;

        setMaster({
          Master_ID: data.master.Master_ID,
          CustomerObj: customerObj,
          ItemObj: itemObj,
          Date: data.master.Date ? data.master.Date.split("T")[0] : "",
          SO: details.SO || "",
          Qty: details.Qty || 0,
          Unit: details.Unit || "Lbs",
          Price: details.Price === null || details.Price === undefined ? "" : details.Price,
          Active: data.master.Active !== undefined ? data.master.Active : true
        });
      } catch (err) {
        console.error("Load edit data error:", err);
      }
    };

    loadEditData();
  }, [editId, accounts, products]);

  const resetForm = () => {
    setMaster({
      Master_ID: "",
      CustomerObj: null,
      ItemObj: null,
      Date: "",
      SO: "",
      Qty: 0,
      Unit: "Lbs",
      Price: "",
      Active: true
    });
    setEditId(null);
  };

  const saveOrder = async () => {
    if (!master.CustomerObj || !master.ItemObj) {
      alert("Please select Customer and Item");
      return;
    }

    const payload = {
      master: {
        Master_ID: master.Master_ID,
        Customer_ID: master.CustomerObj.id,
        Name: master.CustomerObj.SubAccount_Name,
        Date: master.Date,
        Active: master.Active
      },
      details: [
        {
          Item_ID: master.ItemObj.Item_ID,
          Item_Description: master.ItemObj.Product_Description,
          SO: master.SO,
          Qty: master.Qty,
          Unit: master.Unit || null,
          Price: master.Price === "" ? null : Number(master.Price)
        }
      ]
    };

    try {
      const wasEdit = !!editId;
      if (wasEdit) {
        await api.put(`/salesOrders/${editId}`, payload);
      } else {
        await api.post("/salesOrders", payload);
      }
      resetForm();
      await reload();
      alert(wasEdit ? "Sales Order updated" : "Sales Order saved");
    } catch (err) {
      console.error("Save order error:", err);
      alert("Error saving sales order");
    }
  };

  return (
    <Box p={2}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">
          {editId ? "Edit Sale Order" : "Add Sale Order"}
        </Typography>

        <Grid container spacing={2} mt={1}>
          {/* Date */}
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={master.Date}
              onChange={e =>
                setMaster({ ...master, Date: e.target.value })
              }
            />
          </Grid>

          {/* Customer */}
          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <Autocomplete
              options={accounts}
              getOptionLabel={o => o?.SubAccount_Name || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={master.CustomerObj}
              onChange={(e, v) => setMaster({ ...master, CustomerObj: v })}
              renderInput={params => <TextField {...params} label="Customer" fullWidth />}
              fullWidth
              sx={{ width: "100%", minWidth: { xs: "100%", sm: 280 } }}
            />
          </Grid>

          {/* Item */}
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <Autocomplete
              options={products}
              getOptionLabel={o => o?.Product_Description || ""}
              isOptionEqualToValue={(opt, val) => opt?.Item_ID === val?.Item_ID}
              value={master.ItemObj}
              onChange={(e, v) => setMaster({ ...master, ItemObj: v })}
              renderInput={params => <TextField {...params} label="Item" fullWidth />}
              fullWidth
              sx={{ width: "100%", minWidth: { xs: "100%", sm: 240 } }}
            />
          </Grid>

          {/* Customer PO */}
          <Grid size={{ xs: 6, sm: 4, md: 3 }}>
            <TextField
              label="Customer PO #"
              fullWidth
              value={master.SO}
              onChange={e => setMaster({ ...master, SO: e.target.value })}
            />
          </Grid>

          {/* Qty */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <TextField
              type="number"
              label="Qty"
              fullWidth
              value={master.Qty}
              onChange={e =>
                setMaster({ ...master, Qty: Number(e.target.value || 0) })
              }
            />
          </Grid>

          {/* Unit */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <TextField
              select
              label="Unit"
              fullWidth
              value={master.Unit || "Lbs"}
              onChange={e => setMaster({ ...master, Unit: e.target.value })}
            >
              {UNITS.map(u => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Price */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <TextField
              type="number"
              label="Price"
              fullWidth
              inputProps={{ step: "0.01", min: 0 }}
              value={master.Price}
              onChange={e =>
                setMaster({ ...master, Price: e.target.value })
              }
            />
          </Grid>

          {/* Active */}
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={master.Active}
                  onChange={e =>
                    setMaster({ ...master, Active: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <Button variant="contained" onClick={saveOrder}>
            {editId ? "Update" : "Save"}
          </Button>
          {editId && (
            <Button sx={{ ml: 2 }} onClick={resetForm}>
              Cancel
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

/* ===== SALE ORDER LIST ===== */
function SaleOrderList({ rows, onEdit, reload, filters, setFilters, accounts }) {
  const del = async id => {
    if (!window.confirm("Delete this sales order?")) return;
    try {
      await api.delete(`/salesOrders/${id}`); // Soft delete
      reload();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error deleting sales order");
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h6" sx={{ mb: 1 }}>Sales Orders</Typography>
      <OrderFilterBar filters={filters} setFilters={setFilters} onSearch={reload} accounts={accounts} />
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Customer PO</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Active</TableCell> {/* ✅ Active */}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.Master_ID}>
              <TableCell>{r.SO}</TableCell>
              <TableCell>{r.Name}</TableCell>
              <TableCell>{r.Item}</TableCell>
              <TableCell>{r.Date}</TableCell>
              <TableCell>{r.Qty}</TableCell>
              <TableCell>{r.Unit || "-"}</TableCell>
              <TableCell>{r.Price === "" || r.Price === null || r.Price === undefined ? "-" : Number(r.Price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</TableCell>
              <TableCell>{r.Active ? "Yes" : "No"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Button size="small" color="success" onClick={() => openAuthPdf(`/salesOrders/${r.Master_ID}/pdf`)}>
                  View
                </Button>
                <Button size="small" onClick={() => onEdit(r.Master_ID)}>
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => del(r.Master_ID)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  );
}
